(function () {
  // ---------------------------------------------------------------------------
  // Cart drawer upsell ("Pet parents also add:") — dismiss and carousel
  // handling only.
  // Add-to-cart on the card is handled by sales-direct-add.js via the
  // data-sales-* attributes rendered in snippets/cart-upsell.liquid.
  //
  // Dismissal lasts for the browser session. Two mechanisms keep it stable:
  // - The element carries data-skip-node-update, so the drawer's morph on
  //   cart changes leaves its attributes (including `hidden`) untouched.
  // - sessionStorage covers fresh page loads and the cases where the morph
  //   recreates the element (e.g. the drawer switching between its empty and
  //   non-empty layouts), since connectedCallback re-checks it.
  // ---------------------------------------------------------------------------
  var DISMISS_KEY = 'cart-upsell:dismissed';

  function isDismissed() {
    try {
      return window.sessionStorage.getItem(DISMISS_KEY) === '1';
    } catch (error) {
      return false;
    }
  }

  function rememberDismissed() {
    try {
      window.sessionStorage.setItem(DISMISS_KEY, '1');
    } catch (error) {
      // Storage unavailable (privacy mode) — dismissal still applies to this
      // page view via the hidden attribute.
    }
  }

  function scrollUpsell(control, direction) {
    var section = control.closest('cart-upsell-component');
    var scroller = section ? section.querySelector('[data-cart-upsell-scroll]') : null;
    if (!(scroller instanceof HTMLElement)) return;

    var card = scroller.querySelector('.cart-upsell__card');
    var cardWidth = card instanceof HTMLElement ? card.getBoundingClientRect().width : 320;
    var styles = window.getComputedStyle(scroller);
    var gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;

    scroller.scrollBy({
      left: direction * (cardWidth + gap),
      behavior: 'smooth'
    });

    window.requestAnimationFrame(function () {
      updateUpsellControls(section);
    });
  }

  function getCardSignature(section) {
    return Array.prototype.map
      .call(section.querySelectorAll('.cart-upsell__card'), function (card) {
        return card.getAttribute('data-sales-product-id') || card.getAttribute('data-sales-variant-id') || '';
      })
      .join('|');
  }

  function updateUpsellControls(section) {
    var scroller = section.querySelector('[data-cart-upsell-scroll]');
    var previous = section.querySelector('[data-cart-upsell-prev]');
    var next = section.querySelector('[data-cart-upsell-next]');
    var cards = section.querySelectorAll('.cart-upsell__card');

    section.classList.toggle('cart-upsell--carousel', cards.length > 1);

    if (!(scroller instanceof HTMLElement) || !(previous instanceof HTMLElement) || !(next instanceof HTMLElement)) return;

    var maxScrollLeft = scroller.scrollWidth - scroller.clientWidth;
    var scrollLeft = scroller.scrollLeft;
    var canScroll = cards.length > 1 && maxScrollLeft > 1;

    previous.hidden = !canScroll || scrollLeft <= 1;
    next.hidden = !canScroll || scrollLeft >= maxScrollLeft - 1;
  }

  class CartUpsellComponent extends HTMLElement {
    #scroller = null;
    #cardSignature = '';
    #mutationObserver = null;
    #syncFrame = 0;

    #onScroll = () => {
      this.#scheduleSync();
    };

    #onResize = () => {
      this.#scheduleSync();
    };

    #onCartUpdate = () => {
      this.#scheduleSync();
    };

    #onClick = (event) => {
      var target = event.target instanceof Element ? event.target : null;
      if (!target) return;

      var previous = target.closest('[data-cart-upsell-prev]');
      if (previous instanceof HTMLElement) {
        event.preventDefault();
        scrollUpsell(previous, -1);
        return;
      }

      var next = target.closest('[data-cart-upsell-next]');
      if (next instanceof HTMLElement) {
        event.preventDefault();
        scrollUpsell(next, 1);
        return;
      }

      if (!target.closest('.cart-upsell__close')) return;

      rememberDismissed();
      this.hidden = true;
    };

    #bindScroller() {
      var nextScroller = this.querySelector('[data-cart-upsell-scroll]');
      if (nextScroller === this.#scroller) return;

      if (this.#scroller instanceof HTMLElement) {
        this.#scroller.removeEventListener('scroll', this.#onScroll);
      }

      this.#scroller = nextScroller instanceof HTMLElement ? nextScroller : null;

      if (this.#scroller instanceof HTMLElement) {
        this.#scroller.addEventListener('scroll', this.#onScroll, { passive: true });
      }
    }

    #sync() {
      this.#syncFrame = 0;
      this.#bindScroller();

      var nextSignature = getCardSignature(this);
      if (this.#cardSignature && nextSignature !== this.#cardSignature && this.#scroller instanceof HTMLElement) {
        this.#scroller.scrollLeft = 0;
      }
      this.#cardSignature = nextSignature;

      if (isDismissed()) {
        this.hidden = true;
      }

      updateUpsellControls(this);
    }

    #scheduleSync() {
      if (this.#syncFrame) return;

      this.#syncFrame = window.requestAnimationFrame(() => {
        this.#sync();
        window.requestAnimationFrame(() => updateUpsellControls(this));
      });
    }

    connectedCallback() {
      if (isDismissed()) {
        this.hidden = true;
      }

      this.addEventListener('click', this.#onClick);
      document.addEventListener('cart:update', this.#onCartUpdate);
      window.addEventListener('resize', this.#onResize);

      this.#mutationObserver = new MutationObserver(() => this.#scheduleSync());
      this.#mutationObserver.observe(this, { childList: true, subtree: true });

      this.#bindScroller();
      this.#cardSignature = getCardSignature(this);
      this.#scheduleSync();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.#onClick);
      document.removeEventListener('cart:update', this.#onCartUpdate);
      window.removeEventListener('resize', this.#onResize);

      if (this.#scroller instanceof HTMLElement) {
        this.#scroller.removeEventListener('scroll', this.#onScroll);
      }
      if (this.#mutationObserver) {
        this.#mutationObserver.disconnect();
      }
      if (this.#syncFrame) {
        window.cancelAnimationFrame(this.#syncFrame);
      }

      this.#scroller = null;
      this.#mutationObserver = null;
      this.#syncFrame = 0;
    }
  }

  if (!customElements.get('cart-upsell-component')) {
    customElements.define('cart-upsell-component', CartUpsellComponent);
  }
})();
